import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Snackbar,
  Avatar,
  Chip,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  SmartToy as BotIcon,
  Psychology as PsychologyIcon,
  Language as LanguageIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Memory as MemoryIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import axios from '../../utils/axiosConfig';
import { useUserRole } from '../../contexts/UserRoleContext';

const BotConfiguration = () => {
  const { isAdmin, isCustomer, userCompany } = useUserRole();
  
  // State Management
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Bot Management
  const [availableBots, setAvailableBots] = useState([]);
  const [selectedBotId, setSelectedBotId] = useState(null);
  const [selectedBot, setSelectedBot] = useState(null);
  const [botConfig, setBotConfig] = useState(null);
  
  // Quick Start Messages
  const [quickStartMessages, setQuickStartMessages] = useState([]);
  const [newQuickMessage, setNewQuickMessage] = useState('');
  
  // Prompt Templates
  const [prompts, setPrompts] = useState([]);
  const [newPrompt, setNewPrompt] = useState({
    name: '',
    content: '',
    category: 'general',
    variables: []
  });

  // Default Bot Configuration
  const defaultBotConfig = {
    name: '',
    description: '',
    system_prompt: 'Du bist ein hilfsreicher KI-Assistent.',
    is_active: true,
    widget_color: '#FFD700',
    widget_position: 'bottom-right',
    knowledge_only_mode: false,
    max_context_length: 4096,
    temperature: '0.7',
    response_timeout: 30,
    enable_memory: true,
    enable_learning: false,
    safety_mode: true,
    profanity_filter: true,
    welcome_message: 'Hallo! Wie kann ich Ihnen helfen?',
    fallback_message: 'Entschuldigung, ich konnte keine passende Antwort in meiner Wissensdatenbank finden.',
    language: 'de',
    personality: 'helpful',
    response_style: 'conversational',
    quick_start_messages: '[]'
  };

  // API Functions
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadBots = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/bots/');
      setAvailableBots(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Bots:', error);
      showSnackbar('Fehler beim Laden der Bots', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  const loadBotConfig = useCallback(async (botId) => {
    try {
      const response = await axios.get(`/api/bots/${botId}/config`);
      setBotConfig(response.data);
      setSelectedBot(response.data);
      
      // Load quick start messages
      if (response.data.quick_start_messages) {
        try {
          const messages = JSON.parse(response.data.quick_start_messages);
          setQuickStartMessages(messages);
          // Also update botConfig
          setBotConfig(prev => ({
            ...prev,
            quick_start_messages: response.data.quick_start_messages
          }));
        } catch (e) {
          setQuickStartMessages([]);
          setBotConfig(prev => ({
            ...prev,
            quick_start_messages: '[]'
          }));
        }
      } else {
        setQuickStartMessages([]);
        setBotConfig(prev => ({
          ...prev,
          quick_start_messages: '[]'
        }));
      }
    } catch (error) {
      console.error('Fehler beim Laden der Bot-Konfiguration:', error);
      showSnackbar('Fehler beim Laden der Bot-Konfiguration', 'error');
    }
  }, [showSnackbar]);

  const loadPrompts = useCallback(async () => {
    try {
      const response = await axios.get('/api/prompts/');
      setPrompts(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Prompts:', error);
      showSnackbar('Fehler beim Laden der Prompts', 'error');
    }
  }, [showSnackbar]);

  // Quick Start Messages Functions
  const addQuickMessage = useCallback(() => {
    if (newQuickMessage.trim()) {
      const updatedMessages = [...quickStartMessages, newQuickMessage.trim()];
      setQuickStartMessages(updatedMessages);
      setNewQuickMessage('');
      
      // Update botConfig as well
      if (botConfig) {
        setBotConfig(prev => ({
          ...prev,
          quick_start_messages: JSON.stringify(updatedMessages)
        }));
      }
    }
  }, [quickStartMessages, newQuickMessage, botConfig]);

  const removeQuickMessage = useCallback((index) => {
    const updatedMessages = quickStartMessages.filter((_, i) => i !== index);
    setQuickStartMessages(updatedMessages);
    
    // Update botConfig as well
    if (botConfig) {
      setBotConfig(prev => ({
        ...prev,
        quick_start_messages: JSON.stringify(updatedMessages)
      }));
    }
  }, [quickStartMessages, botConfig]);

  // Event Handlers
  const handleBotSelect = useCallback((botId) => {
    setSelectedBotId(botId);
    if (botId) {
      loadBotConfig(botId);
    } else {
      setSelectedBot(null);
      setBotConfig(null);
    }
  }, [loadBotConfig]);

  const handleConfigChange = useCallback((field, value) => {
    if (botConfig) {
      setBotConfig(prev => ({
        ...prev,
        [field]: value
      }));
    }
  }, [botConfig]);

  const handleSaveBotConfig = useCallback(async () => {
    if (!selectedBotId || !botConfig) {
      showSnackbar('Bitte wählen Sie einen Bot aus', 'error');
      return;
    }

    try {
      setSaving(true);
      
      // Validiere die Konfiguration
      const requiredFields = ['name', 'system_prompt', 'welcome_message', 'fallback_message'];
      for (const field of requiredFields) {
        if (!botConfig[field] || botConfig[field].trim() === '') {
          showSnackbar(`Bitte füllen Sie das Feld "${field}" aus`, 'error');
          return;
        }
      }

      // Bereite die Daten für das Backend vor
      const updateData = {
        name: botConfig.name,
        description: botConfig.description || '',
        system_prompt: botConfig.system_prompt,
        is_active: Boolean(botConfig.is_active),
        widget_color: botConfig.widget_color,
        widget_position: botConfig.widget_position,
        knowledge_only_mode: Boolean(botConfig.knowledge_only_mode),
        max_context_length: parseInt(botConfig.max_context_length) || 4096,
        temperature: String(botConfig.temperature),
        response_timeout: parseInt(botConfig.response_timeout) || 30,
        enable_memory: Boolean(botConfig.enable_memory),
        enable_learning: Boolean(botConfig.enable_learning),
        safety_mode: Boolean(botConfig.safety_mode),
        profanity_filter: Boolean(botConfig.profanity_filter),
        welcome_message: botConfig.welcome_message,
        fallback_message: botConfig.fallback_message,
        language: botConfig.language,
        personality: botConfig.personality,
        response_style: botConfig.response_style,
        quick_start_messages: botConfig.quick_start_messages || JSON.stringify(quickStartMessages)
      };

      await axios.put(`/api/bots/${selectedBotId}`, updateData);
      showSnackbar('Bot-Konfiguration erfolgreich gespeichert', 'success');
      
      // Lade die aktualisierten Bot-Daten
      await loadBots();
      await loadBotConfig(selectedBotId);
    } catch (error) {
      console.error('Fehler beim Speichern der Bot-Konfiguration:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Speichern';
      showSnackbar(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  }, [selectedBotId, botConfig, showSnackbar, loadBots, loadBotConfig]);

  const handleSavePrompt = useCallback(async () => {
    if (!newPrompt.name || !newPrompt.content) {
      showSnackbar('Bitte füllen Sie Name und Inhalt aus', 'error');
      return;
    }

    try {
      setSaving(true);
      await axios.post('/api/prompts/', newPrompt);
      showSnackbar('Prompt erfolgreich gespeichert', 'success');
      setNewPrompt({ name: '', content: '', category: 'general', variables: [] });
      await loadPrompts();
    } catch (error) {
      console.error('Fehler beim Speichern des Prompts:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Fehler beim Speichern';
      showSnackbar(errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  }, [newPrompt, showSnackbar, loadPrompts]);

  // Effects
  useEffect(() => {
    loadBots();
    loadPrompts();
  }, [loadBots, loadPrompts]);

  // Helper Components
  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  const ConfigField = ({ label, field, type = 'text', options = null, multiline = false, helperText = '' }) => {
    if (type === 'select' && options) {
      return (
        <FormControl fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            value={botConfig?.[field] || ''}
            onChange={(e) => handleConfigChange(field, e.target.value)}
            label={label}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {helperText && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {helperText}
            </Typography>
          )}
        </FormControl>
      );
    }

    if (type === 'switch') {
      return (
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(botConfig?.[field])}
              onChange={(e) => handleConfigChange(field, e.target.checked)}
            />
          }
          label={label}
        />
      );
    }

    return (
      <TextField
        label={label}
        type={type}
        value={botConfig?.[field] || ''}
        onChange={(e) => handleConfigChange(field, e.target.value)}
        fullWidth
        multiline={multiline}
        rows={multiline ? 3 : 1}
        helperText={helperText}
      />
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BotIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Bot Konfiguration</Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<RefreshIcon />}
          onClick={loadBots}
          disabled={loading}
          sx={{ borderRadius: 2 }}
        >
          {loading ? 'Lade...' : 'Aktualisieren'}
        </Button>
      </Box>

      {/* Bot-Auswahl */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BotIcon />
            Bot auswählen
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Bot auswählen</InputLabel>
            <Select
              value={selectedBotId || ''}
              onChange={(e) => handleBotSelect(e.target.value)}
              label="Bot auswählen"
              disabled={loading}
            >
              <MenuItem value="">
                <em>Kein Bot ausgewählt</em>
              </MenuItem>
              {availableBots.map((bot) => (
                <MenuItem key={bot.id} value={bot.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                      <BotIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {bot.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {bot.description || 'Keine Beschreibung'}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!selectedBotId && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Wählen Sie einen Bot aus, um dessen Konfiguration zu bearbeiten.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Bot-Konfiguration */}
      {selectedBotId && botConfig ? (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {botConfig.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {botConfig.description || 'Keine Beschreibung'}
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={handleSaveBotConfig}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {saving ? 'Speichere...' : 'Konfiguration speichern'}
              </Button>
            </Box>

            <Grid container spacing={3}>
              {/* Grundlegende Einstellungen */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SettingsIcon />
                      Grundlegende Einstellungen
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <ConfigField
                        label="Bot-Name"
                        field="name"
                        helperText="Name des Bots, der in der Benutzeroberfläche angezeigt wird"
                      />
                      <ConfigField
                        label="Beschreibung"
                        field="description"
                        multiline
                        helperText="Kurze Beschreibung des Bots"
                      />
                      <ConfigField
                        label="System-Prompt"
                        field="system_prompt"
                        multiline
                        helperText="Grundlegende Anweisungen für das KI-Modell"
                      />
                      <ConfigField
                        label="Aktiv"
                        field="is_active"
                        type="switch"
                        helperText="Ob der Bot für Benutzer verfügbar ist"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* KI-Einstellungen */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PsychologyIcon />
                      KI-Einstellungen
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <ConfigField
                        label="Temperatur"
                        field="temperature"
                        type="number"
                        helperText="0.0 = sehr fokussiert, 2.0 = sehr kreativ"
                      />
                      <ConfigField
                        label="Max. Kontextlänge"
                        field="max_context_length"
                        type="number"
                        helperText="Maximale Anzahl der Tokens im Kontext"
                      />
                      <ConfigField
                        label="Antwort-Timeout (Sekunden)"
                        field="response_timeout"
                        type="number"
                        helperText="Maximale Wartezeit für eine Antwort"
                      />
                      <ConfigField
                        label="Sprache"
                        field="language"
                        type="select"
                        options={[
                          { value: 'de', label: 'Deutsch' },
                          { value: 'en', label: 'English' },
                          { value: 'fr', label: 'Français' },
                          { value: 'es', label: 'Español' }
                        ]}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Bot-Verhalten */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LanguageIcon />
                      Bot-Verhalten
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <ConfigField
                        label="Persönlichkeit"
                        field="personality"
                        type="select"
                        options={[
                          { value: 'helpful', label: 'Hilfsreich' },
                          { value: 'professional', label: 'Professionell' },
                          { value: 'friendly', label: 'Freundlich' },
                          { value: 'technical', label: 'Technisch' }
                        ]}
                      />
                      <ConfigField
                        label="Antwort-Stil"
                        field="response_style"
                        type="select"
                        options={[
                          { value: 'conversational', label: 'Gesprächig' },
                          { value: 'formal', label: 'Formell' },
                          { value: 'casual', label: 'Locker' },
                          { value: 'technical', label: 'Technisch' }
                        ]}
                      />
                      
                      {/* Quick Start Messages */}
                      <Box sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SettingsIcon />
                          Automatische Anfangsnachrichten
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Diese Nachrichten werden als Buttons über dem Eingabefeld angezeigt, nur bei der ersten Nachricht.
                        </Typography>
                        
                        {/* Add new message */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          <TextField
                            label="Neue Anfangsnachricht"
                            value={newQuickMessage}
                            onChange={(e) => setNewQuickMessage(e.target.value)}
                            fullWidth
                            size="small"
                            onKeyPress={(e) => e.key === 'Enter' && addQuickMessage()}
                          />
                          <Button
                            variant="contained"
                            onClick={addQuickMessage}
                            disabled={!newQuickMessage.trim()}
                            size="small"
                          >
                            Hinzufügen
                          </Button>
                        </Box>
                        
                        {/* List existing messages */}
                        {quickStartMessages.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              Aktuelle Anfangsnachrichten:
                            </Typography>
                            {quickStartMessages.map((message, index) => (
                              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="body2" sx={{ flex: 1, p: 1, backgroundColor: 'white', borderRadius: 1, border: '1px solid #ddd' }}>
                                  {message}
                                </Typography>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => removeQuickMessage(index)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            ))}
                          </Box>
                        )}
                        
                        {quickStartMessages.length === 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            Noch keine Anfangsnachrichten konfiguriert.
                          </Typography>
                        )}
                      </Box>
                      <ConfigField
                        label="Willkommensnachricht"
                        field="welcome_message"
                        multiline
                        helperText="Erste Nachricht, die der Bot sendet"
                      />
                      <ConfigField
                        label="Fallback-Nachricht"
                        field="fallback_message"
                        multiline
                        helperText="Nachricht bei unbekannten Anfragen"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Wissensdatenbank & Sicherheit */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SecurityIcon />
                      Wissensdatenbank & Sicherheit
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <ConfigField
                        label="Nur Wissensdatenbank"
                        field="knowledge_only_mode"
                        type="switch"
                        helperText="Bot antwortet nur basierend auf der Wissensdatenbank"
                      />
                      <ConfigField
                        label="Gesprächsspeicher"
                        field="enable_memory"
                        type="switch"
                        helperText="Bot merkt sich vorherige Nachrichten"
                      />
                      <ConfigField
                        label="Lernmodus"
                        field="enable_learning"
                        type="switch"
                        helperText="Bot lernt aus Gesprächen"
                      />
                      <ConfigField
                        label="Sicherheitsmodus"
                        field="safety_mode"
                        type="switch"
                        helperText="Aktiviert Sicherheitsfilter"
                      />
                      <ConfigField
                        label="Schimpfwort-Filter"
                        field="profanity_filter"
                        type="switch"
                        helperText="Filtert unangemessene Inhalte"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : selectedBotId ? (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Bot-Konfiguration wird geladen...
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 3 }}>
          Bitte wählen Sie einen Bot aus, um dessen Konfiguration zu bearbeiten.
        </Alert>
      )}

      {/* Prompt Templates */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LanguageIcon />
            Prompt Templates
          </Typography>
          
          <Grid container spacing={3}>
            {/* Neuer Prompt */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Neuer Prompt Template
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Name"
                      value={newPrompt.name}
                      onChange={(e) => setNewPrompt({ ...newPrompt, name: e.target.value })}
                      fullWidth
                    />
                    <FormControl fullWidth>
                      <InputLabel>Kategorie</InputLabel>
                      <Select
                        value={newPrompt.category}
                        onChange={(e) => setNewPrompt({ ...newPrompt, category: e.target.value })}
                        label="Kategorie"
                      >
                        <MenuItem value="general">Allgemein</MenuItem>
                        <MenuItem value="customer_service">Kundenservice</MenuItem>
                        <MenuItem value="sales">Verkauf</MenuItem>
                        <MenuItem value="technical">Technisch</MenuItem>
                        <MenuItem value="creative">Kreativ</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label="Prompt Inhalt"
                      value={newPrompt.content}
                      onChange={(e) => setNewPrompt({ ...newPrompt, content: e.target.value })}
                      fullWidth
                      multiline
                      rows={6}
                      helperText="Verwenden Sie {{variable}} für dynamische Inhalte"
                    />
                    <Button 
                      variant="contained" 
                      onClick={handleSavePrompt}
                      disabled={!newPrompt.name || !newPrompt.content || saving}
                      startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      {saving ? 'Speichere...' : 'Prompt speichern'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Bestehende Prompts */}
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Bestehende Prompts
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Kategorie</TableCell>
                          <TableCell>Aktionen</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {prompts.map((prompt) => (
                          <TableRow key={prompt.id}>
                            <TableCell>{prompt.name}</TableCell>
                            <TableCell>
                              <Chip label={prompt.category} size="small" />
                            </TableCell>
                            <TableCell>
                              <IconButton size="small">
                                <EditIcon />
                              </IconButton>
                              <IconButton size="small" color="error">
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BotConfiguration;